import React from "react";
import {
  Users,
  BookOpen,
  Star,
  FileText,
  ChevronRight,
  Layout,
  PlayCircle,
  Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import { useInstructorStudentsQuery } from "../../hooks/queries/useCurriculumAdminQueries";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/147/147142.png";

const GlassCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const InstructorDashboard = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useInstructorStudentsQuery();

  const kpis = data?.kpis;
  const recentStudents = (data?.students || []).slice(0, 4);

  return (
    <div className="cht-space-y-10 cht-animate-in cht-fade-in cht-duration-700">
      {/* Welcome Hero Section */}
      <header className="cht-relative cht-overflow-hidden cht-rounded-[40px] cht-bg-[#3e57da] cht-p-10 md:cht-p-16 cht-text-white">
        <div className="cht-relative cht-z-10 cht-max-w-2xl cht-space-y-6">
          <div className="cht-inline-flex cht-items-center cht-gap-2 cht-px-4 cht-py-1.5 cht-bg-white/10 cht-backdrop-blur-md cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest">
            <Zap size={12} className="cht-text-amber-400" /> Instructor Console
            v2.0
          </div>
          <h1 className="cht-text-4xl md:cht-text-5xl cht-font-black cht-tracking-tight cht-leading-tight">
            Empower your students with{" "}
            <span className="cht-text-blue-200">exceptional learning</span>{" "}
            experiences.
          </h1>
          <p className="cht-text-blue-50 cht-font-medium cht-text-lg cht-opacity-80">
            Welcome back! You have {kpis?.submissions_count ?? 0} assignment
            submissions across {kpis?.active_courses_count ?? 0} assigned
            programs.
          </p>
          <div className="cht-flex cht-flex-wrap cht-gap-4 cht-pt-4">
            <Button
              variant="secondary"
              className="cht-bg-white cht-text-[#3e57da] hover:cht-bg-blue-50 cht-rounded-2xl cht-px-8"
              onClick={() => navigate("/instructor/dashboard/my-courses")}
            >
              Manage Courses
            </Button>
            <Button
              variant="outline"
              className="cht-border-white/20 cht-text-white hover:cht-bg-white/10 cht-rounded-2xl cht-px-8"
              onClick={() => navigate("/instructor/dashboard/students")}
            >
              View Students
            </Button>
          </div>
        </div>
        {/* Abstract Background Decoration */}
        <div className="cht-absolute cht-right-0 cht-top-0 cht-w-1/2 cht-h-full cht-opacity-10 cht-pointer-events-none">
          <Layout className="cht-w-full cht-h-full cht-scale-150 -cht-rotate-12" />
        </div>
      </header>

      {/* KPI Cockpit */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        {[
          {
            label: "Total Students",
            value: isLoading ? "—" : String(kpis?.total_students ?? 0),
            icon: Users,
            color: "blue",
            trend: `${kpis?.total_enrollments ?? 0} enrollments`,
          },
          {
            label: "Active Programs",
            value: isLoading ? "—" : String(kpis?.active_courses_count ?? 0),
            icon: BookOpen,
            color: "purple",
            trend: "Live DB",
          },
          {
            label: "Lessons & Tasks",
            value: isLoading ? "—" : String(kpis?.total_lessons_count ?? 0),
            icon: PlayCircle,
            color: "green",
            trend: "Curriculum",
          },
          {
            label: "Avg. Rating",
            value: isLoading
              ? "—"
              : Number(kpis?.avg_rating) > 0
              ? Number(kpis?.avg_rating).toFixed(1)
              : "0.0",
            icon: Star,
            color: "amber",
            trend: "Out of 5.0",
          },
        ].map((stat, i) => (
          <GlassCard
            key={i}
            className="cht-p-8 cht-rounded-[32px] cht-space-y-4 cht-group hover:cht-border-[#3e57da]/20 cht-transition-all"
          >
            <div
              className={`cht-w-12 cht-h-12 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-bg-${stat.color}-50 cht-text-${stat.color}-500 group-hover:cht-scale-110 cht-transition-transform`}
            >
              <stat.icon size={24} />
            </div>
            <div className="cht-space-y-1">
              <div className="cht-flex cht-items-center cht-justify-between">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  {stat.label}
                </p>
                <span className="cht-text-[10px] cht-font-black cht-text-green-500">
                  {stat.trend}
                </span>
              </div>
              <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
                {stat.value}
              </h4>
            </div>
          </GlassCard>
        ))}
      </div>

      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8">
        {/* Engagement Analytics Chart */}
        <GlassCard className="lg:cht-col-span-8 cht-p-10 cht-rounded-[40px] cht-flex cht-flex-col">
          <div className="cht-flex items-center cht-justify-between cht-mb-10">
            <div className="cht-space-y-1">
              <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-tracking-tight">
                Student Engagement
              </h3>
              <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                Average completion progress:{" "}
                <span className="cht-font-bold cht-text-[#3e57da]">
                  {kpis?.average_progress ?? 0}%
                </span>{" "}
                ({kpis?.completed_count ?? 0} completed,{" "}
                {kpis?.in_progress_count ?? 0} in progress)
              </p>
            </div>
            <div className="cht-flex cht-gap-2">
              {["7D", "30D", "90D"].map((t) => (
                <button
                  key={t}
                  className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                    t === "30D"
                      ? "cht-bg-[#3e57da] cht-text-white"
                      : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="cht-flex-1 cht-min-h-[300px] cht-relative cht-flex cht-items-end cht-gap-4">
            {[40, 60, 45, 90, 65, 80, 50, 70, 85, 100, 75, 60].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ delay: i * 0.05, duration: 0.8 }}
                className="cht-flex-1 cht-bg-[#3e57da]/10 cht-rounded-t-lg cht-relative cht-group"
              >
                <div className="cht-absolute cht-inset-x-0 cht-bottom-0 cht-h-full cht-bg-[#3e57da] cht-rounded-t-lg cht-scale-y-0 group-hover:cht-scale-y-100 cht-origin-bottom cht-transition-transform cht-duration-500" />
              </motion.div>
            ))}
          </div>
        </GlassCard>

        {/* Action Sidebar */}
        <div className="lg:cht-col-span-4 cht-space-y-8">
          <GlassCard className="cht-p-8 cht-rounded-[40px] cht-space-y-6">
            <h3 className="cht-text-sm cht-font-black cht-uppercase cht-tracking-widest">
              Recent Enrollments
            </h3>
            <div className="cht-space-y-5">
              {recentStudents.length > 0 ? (
                recentStudents.map((student) => (
                  <div
                    key={student.id}
                    onClick={() => navigate("/instructor/dashboard/students")}
                    className="cht-flex cht-items-center cht-gap-4 cht-cursor-pointer hover:cht-opacity-80 cht-transition-opacity"
                  >
                    <img
                      src={student.avatar || DEFAULT_AVATAR}
                      alt={student.name}
                      className="cht-w-10 cht-h-10 cht-rounded-xl cht-object-cover cht-bg-gray-100 cht-flex-shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          DEFAULT_AVATAR;
                      }}
                    />
                    <div className="cht-flex-1 cht-min-w-0">
                      <p className="cht-text-sm cht-font-bold cht-text-gray-900 cht-truncate">
                        {student.name}
                      </p>
                      <p className="cht-text-[10px] cht-text-[#3e57da] cht-font-bold cht-uppercase cht-tracking-widest cht-truncate">
                        {student.course}
                      </p>
                    </div>
                    <span className="cht-text-[9px] cht-font-bold cht-text-gray-400 cht-uppercase cht-whitespace-nowrap">
                      {student.lastActive}
                    </span>
                  </div>
                ))
              ) : (
                <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                  {isLoading
                    ? "Loading recent enrollments..."
                    : "No recent student enrollments found."}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/instructor/dashboard/students")}
              className="cht-w-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-pt-4"
            >
              View All Students
            </Button>
          </GlassCard>

          <GlassCard className="cht-p-8 cht-rounded-[40px] cht-bg-gradient-to-br cht-from-[#3e57da] cht-to-[#2d41a8] cht-text-white cht-space-y-4">
            <h3 className="cht-text-sm cht-font-black cht-uppercase cht-tracking-widest">
              Student Submissions
            </h3>
            <div className="cht-space-y-4">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <FileText size={18} className="cht-text-blue-200" />
                  <span className="cht-text-sm cht-font-bold">
                    {kpis?.submissions_count ?? 0} Assignments
                  </span>
                </div>
                <ChevronRight size={16} className="cht-opacity-50" />
              </div>
              <p className="cht-text-[11px] cht-text-blue-100 cht-opacity-70">
                Inspect student repositories, automated test scores, and module
                deliverables.
              </p>
              <Button
                variant="outline"
                className="cht-w-full cht-border-white/20 cht-text-white hover:cht-bg-white/10 cht-rounded-xl cht-mt-2"
                onClick={() => navigate("/instructor/dashboard/assignments")}
              >
                Go to Grading
              </Button>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default InstructorDashboard;
